import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMutation } from './single-flight';
import type { QueryKey } from '@tanstack/react-query';
import { useDataSource } from './data-source';
import type { EmailPreferences, EmailPreferencesChange } from './types';
import type { LinkNote, UnsubscribeCategory } from './notifications-api';

/** The lifecycle mail's choices (docs/engineering/lifecycle-emails.md): the person's own, or a link's. */
export const emailPreferenceKeys = {
  mine: (): QueryKey => ['email-preferences', 'me'],
  link: (token: string): QueryKey => ['email-preferences', 'link', token],
};

export function useEmailPreferences() {
  const ds = useDataSource();
  return useQuery({
    queryKey: emailPreferenceKeys.mine(),
    queryFn: () => ds.notifications.emailPreferences(),
  });
}

/** A change answers the choices as they now stand, which go straight into the cache. */
export function useSetEmailPreferences(tenant: string) {
  const ds = useDataSource();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (change: EmailPreferencesChange) =>
      ds.notifications.setEmailPreferences(tenant, change),
    onSuccess: (now: EmailPreferences) => qc.setQueryData(emailPreferenceKeys.mine(), now),
  });
}

export function useLinkPreferences(token: string) {
  const ds = useDataSource();
  return useQuery({
    queryKey: emailPreferenceKeys.link(token),
    queryFn: () => ds.notifications.linkPreferences(token),
    retry: false,
  });
}

export function useSetLinkPreferences(token: string) {
  const ds = useDataSource();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (change: EmailPreferencesChange) =>
      ds.notifications.setLinkPreferences(token, change),
    onSuccess: (now: EmailPreferences) => qc.setQueryData(emailPreferenceKeys.link(token), now),
  });
}

/** The page's Unsubscribe button: the category off (a learning note's: that note), then the choices read again. */
export function useUnsubscribe(token: string, note?: LinkNote) {
  const ds = useDataSource();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (category?: UnsubscribeCategory) =>
      ds.notifications.unsubscribe(
        token,
        category,
        category === 'learning_notes' ? note : undefined,
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: emailPreferenceKeys.link(token) }),
  });
}
